/**
 * PostHog Tracking Verification Component
 * Provides comprehensive testing and verification of PostHog integration
 */

import {
  debugPostHogStatus,
  getFeatureFlagPostHog,
  getSessionReplayUrlPostHog,
  identifyUserByEmailPostHog,
  identifyUserPostHog,
  isFeatureEnabledPostHog,
  isSessionRecordingActivePostHog,
  setUserPropertiesPostHog,
  startSessionRecordingPostHog,
  trackButtonClickPostHog,
  trackCheckoutPanelOpenedPostHog,
  trackConversionPostHog,
  trackEmailInputInteractionPostHog,
  trackErrorPostHog,
  trackLightboxClosedPostHog,
  trackLightboxNavigationPostHog,
  trackLightboxOpenedPostHog,
  trackMediaItemClickPostHog,
  trackPlanButtonClickPostHog,
  trackVideoCompletePostHog,
  trackVideoPlayPostHog,
  trackVideoProgressPostHog,
} from '@/lib/posthogAnalytics';
import { BRAND_SITE_HOST, BRAND_SUPPORT_EMAIL } from '@/lib/brandConfig';
import React, { useEffect, useState } from 'react';

interface PostHogStatus {
  loaded: boolean;
  initialized: boolean;
  distinctId: string | null;
  sessionId: string | null;
  sessionRecording: boolean;
  replayUrl: string | null;
  config: Record<string, unknown> | null;
}

export function PostHogTrackingVerification() {
  const [status, setStatus] = useState<PostHogStatus>({
    loaded: false,
    initialized: false,
    distinctId: null,
    sessionId: null,
    sessionRecording: false,
    replayUrl: null,
    config: null
  });
  
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  const addTestResult = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const prefix = type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️';
    setTestResults(prev => [...prev, `[${timestamp}] ${prefix} ${message}`]);
  };

  const checkPostHogStatus = () => {
    if (typeof window === 'undefined') return;

    const newStatus: PostHogStatus = {
      loaded: !!window.posthog,
      initialized: !!(window.posthog && window.posthog.__loaded),
      distinctId: window.posthog?.get_distinct_id() || null,
      sessionId: window.posthog?.get_session_id() || null,
      sessionRecording: isSessionRecordingActivePostHog(),
      replayUrl: getSessionReplayUrlPostHog(),
      config: window.posthog?.config || null
    };

    setStatus(newStatus);
    debugPostHogStatus();

    // Log status
    if (newStatus.loaded) {
      addTestResult('PostHog loaded successfully', 'success');
      if (newStatus.initialized) {
        addTestResult('PostHog initialized', 'success');
        addTestResult(`Distinct ID: ${newStatus.distinctId}`, 'info');
        if (newStatus.sessionId) {
          addTestResult(`Session ID: ${newStatus.sessionId}`, 'info');
        }
        if (newStatus.sessionRecording) {
          addTestResult('Session recording is active', 'success');
          if (newStatus.replayUrl) {
            addTestResult(`Session replay URL available`, 'success');
          }
        } else {
          addTestResult('Session recording not active', 'error');
        }
      } else {
        addTestResult('PostHog not fully initialized', 'error');
      }
    } else {
      addTestResult('PostHog not loaded', 'error');
    }
  };

  const testBasicTracking = () => {
    try {
      trackButtonClickPostHog(
        'Test Button Click',
        'test-verification-button',
        'verification_test',
        'testing',
        'TEST',
        'VerificationComponent'
      );
      addTestResult('Basic button click tracking sent', 'success');
    } catch (error) {
      addTestResult(`Basic tracking failed: ${error}`, 'error');
    }
  };

  const testConversionTracking = () => {
    try {
      trackConversionPostHog('test_conversion', 'verification_service', 100, 'USD');
      addTestResult('Conversion tracking sent', 'success');
    } catch (error) {
      addTestResult(`Conversion tracking failed: ${error}`, 'error');
    }
  };

  const testUserIdentification = () => {
    try {
      const testUserId = `test_user_${Date.now()}`;
      identifyUserPostHog(testUserId, {
        email: `test@${BRAND_SUPPORT_EMAIL.split('@')[1] || BRAND_SITE_HOST}`,
        name: 'Test User',
        test_account: true,
        verification_source: 'PostHogTrackingVerification'
      });
      addTestResult(`User identified: ${testUserId}`, 'success');
    } catch (error) {
      addTestResult(`User identification failed: ${error}`, 'error');
    }
  };

  const testUserProperties = () => {
    try {
      setUserPropertiesPostHog({
        test_property: 'verification_value',
        last_verification: new Date().toISOString(),
        browser_info: navigator.userAgent,
        screen_resolution: `${screen.width}x${screen.height}`,
        verification_component: true
      });
      addTestResult('User properties set', 'success');
    } catch (error) {
      addTestResult(`User properties failed: ${error}`, 'error');
    }
  };

  const testSessionRecording = () => {
    try {
      if (isSessionRecordingActivePostHog()) {
        addTestResult('Session recording already active', 'info');
      } else {
        startSessionRecordingPostHog();
        addTestResult('Session recording started', 'success');
      }
      
      // Test some interactions
      setTimeout(() => {
        if (window.posthog) {
          window.posthog.capture('session_recording_test_interaction', {
            test_type: 'verification_interaction',
            timestamp: Date.now(),
            interaction_source: 'PostHogTrackingVerification'
          });
          addTestResult('Session recording test interaction captured', 'success');
        }
      }, 1000);
      
    } catch (error) {
      addTestResult(`Session recording test failed: ${error}`, 'error');
    }
  };

  const testFeatureFlags = () => {
    try {
      const testFlag = getFeatureFlagPostHog('test_flag');
      addTestResult(`Feature flag 'test_flag': ${testFlag || 'undefined'}`, 'info');
      
      const testFeature = isFeatureEnabledPostHog('test_feature');
      addTestResult(`Feature 'test_feature' enabled: ${testFeature}`, 'info');
      
      // Reload flags
      if (window.posthog) {
        window.posthog.reloadFeatureFlags();
        addTestResult('Feature flags reloaded', 'success');
      }
    } catch (error) {
      addTestResult(`Feature flags test failed: ${error}`, 'error');
    }
  };

  const testErrorTracking = () => {
    try {
      const testError = new Error('Test error for PostHog verification');
      trackErrorPostHog(testError, {
        test_context: 'verification_component',
        component: 'PostHogTrackingVerification',
        intentional: true
      });
      addTestResult('Error tracking sent', 'success');
    } catch (error) {
      addTestResult(`Error tracking failed: ${error}`, 'error');
    }
  };

  const testMediaGalleryEvents = () => {
    try {
      trackMediaItemClickPostHog(1, 'photo', 'Test Photo', 0, 'BioLinkTemplate', 'US');
      trackLightboxOpenedPostHog(1, 'gallery_click', 'photo', 'BioLinkTemplate', 'US');
      trackLightboxNavigationPostHog('next', 1, 2, 'BioLinkTemplate', 'US');
      trackLightboxClosedPostHog(1, 5, 2, 'BioLinkTemplate', 'US');
      addTestResult('Media gallery events sent', 'success');
    } catch (error) {
      addTestResult(`Media gallery test failed: ${error}`, 'error');
    }
  };

  const testCheckoutFlowEvents = () => {
    try {
      trackPlanButtonClickPostHog('standard', 2999, 'USD', 'test_flow', 'US', false);
      trackCheckoutPanelOpenedPostHog('standard', 'direct_button', 2999, 'USD', 'test_flow', 'US');
      trackEmailInputInteractionPostHog('focus', 'standard', undefined, 'test_flow', 'US');
      trackEmailInputInteractionPostHog('blur', 'standard', true, 'test_flow', 'US');
      addTestResult('Checkout flow events sent', 'success');
    } catch (error) {
      addTestResult(`Checkout flow test failed: ${error}`, 'error');
    }
  };

  const testVideoProgressEvents = () => {
    try {
      trackVideoPlayPostHog('test-video-1', 'Test Video', 'playback_xxx', 'BioLinkTemplate', 'US');
      trackVideoProgressPostHog('test-video-1', 50, 30, 'BioLinkTemplate', 'US');
      trackVideoCompletePostHog('test-video-1', 60, 'BioLinkTemplate', 'US');
      addTestResult('Video progress events sent', 'success');
    } catch (error) {
      addTestResult(`Video progress test failed: ${error}`, 'error');
    }
  };

  const testUserIdentificationByEmail = () => {
    try {
      identifyUserByEmailPostHog(`test@${BRAND_SUPPORT_EMAIL.split('@')[1] || BRAND_SITE_HOST}`, {
        plan_tier: 'standard',
        currency: 'USD',
        country: 'US',
        flow_name: 'verification_test',
        checkout_started_at: new Date().toISOString(),
      });
      addTestResult('User identified by email', 'success');
    } catch (error) {
      addTestResult(`Identify by email failed: ${error}`, 'error');
    }
  };

  const runAllTests = () => {
    setTestResults([]);
    addTestResult('Starting comprehensive PostHog verification...', 'info');
    
    setTimeout(() => checkPostHogStatus(), 100);
    setTimeout(() => testBasicTracking(), 200);
    setTimeout(() => testConversionTracking(), 300);
    setTimeout(() => testUserIdentification(), 400);
    setTimeout(() => testUserProperties(), 500);
    setTimeout(() => testSessionRecording(), 600);
    setTimeout(() => testFeatureFlags(), 700);
    setTimeout(() => testErrorTracking(), 800);
    setTimeout(() => testMediaGalleryEvents(), 900);
    setTimeout(() => testCheckoutFlowEvents(), 1000);
    setTimeout(() => testVideoProgressEvents(), 1100);
    setTimeout(() => testUserIdentificationByEmail(), 1200);

    setTimeout(() => {
      addTestResult('Comprehensive verification completed', 'success');
    }, 1500);
  };

  useEffect(() => {
    // Dev-only auto-check — prod mounts this for tree simplicity but must not run timers/work.
    if (!import.meta.env.DEV) return;
    const t = setTimeout(() => checkPostHogStatus(), 2000);
    return () => clearTimeout(t);
  }, []);

  // Only show in development mode
  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
          background: '#667eea',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
        title="PostHog Verification Panel"
      >
        📊
      </button>

      {/* Verification panel */}
      {isVisible && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          width: '400px',
          maxHeight: '80vh',
          background: '#1a1a2e',
          color: '#cccccc',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '20px',
          zIndex: 9998,
          fontFamily: 'monospace',
          fontSize: '12px',
          overflow: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#667eea' }}>🎯 PostHog Verification</h3>
            <button
              onClick={() => setIsVisible(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#cccccc',
                fontSize: '18px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>

          {/* Status Section */}
          <div style={{ marginBottom: '20px', padding: '10px', background: '#16213e', borderRadius: '5px' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#00ff88' }}>Status</h4>
            <div>Loaded: <span style={{ color: status.loaded ? '#00ff88' : '#ff4444' }}>{status.loaded ? '✅' : '❌'}</span></div>
            <div>Initialized: <span style={{ color: status.initialized ? '#00ff88' : '#ff4444' }}>{status.initialized ? '✅' : '❌'}</span></div>
            <div>Session Recording: <span style={{ color: status.sessionRecording ? '#00ff88' : '#ffaa00' }}>{status.sessionRecording ? '✅' : '⚠️'}</span></div>
            {status.distinctId && <div>Distinct ID: <span style={{ color: '#667eea' }}>{status.distinctId}</span></div>}
            {status.sessionId && <div>Session ID: <span style={{ color: '#667eea' }}>{status.sessionId}</span></div>}
          </div>

          {/* Test Buttons */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#00ff88' }}>Tests</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
              <button onClick={checkPostHogStatus} style={buttonStyle}>Status</button>
              <button onClick={testBasicTracking} style={buttonStyle}>Basic</button>
              <button onClick={testConversionTracking} style={buttonStyle}>Conversion</button>
              <button onClick={testUserIdentification} style={buttonStyle}>Identify</button>
              <button onClick={testUserProperties} style={buttonStyle}>Properties</button>
              <button onClick={testSessionRecording} style={buttonStyle}>Recording</button>
              <button onClick={testFeatureFlags} style={buttonStyle}>Flags</button>
              <button onClick={testErrorTracking} style={buttonStyle}>Errors</button>
              <button onClick={testMediaGalleryEvents} style={buttonStyle}>Media</button>
              <button onClick={testCheckoutFlowEvents} style={buttonStyle}>Checkout</button>
              <button onClick={testVideoProgressEvents} style={buttonStyle}>Video</button>
              <button onClick={testUserIdentificationByEmail} style={buttonStyle}>Email ID</button>
            </div>
            <button 
              onClick={runAllTests} 
              style={{
                ...buttonStyle,
                width: '100%',
                marginTop: '10px',
                background: '#00ff88',
                color: '#000'
              }}
            >
              Run All Tests
            </button>
          </div>

          {/* Results */}
          <div>
            <h4 style={{ margin: '0 0 10px 0', color: '#00ff88' }}>Results</h4>
            <div style={{
              background: '#0a0a1a',
              padding: '10px',
              borderRadius: '5px',
              maxHeight: '200px',
              overflow: 'auto',
              fontSize: '10px',
              lineHeight: '1.4'
            }}>
              {testResults.length === 0 ? (
                <div style={{ color: '#666' }}>No test results yet...</div>
              ) : (
                testResults.map((result, index) => (
                  <div key={index} style={{ marginBottom: '2px' }}>
                    {result}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const buttonStyle: React.CSSProperties = {
  background: '#667eea',
  color: 'white',
  border: 'none',
  padding: '5px 8px',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '10px'
};